<template>
  <div class="flex w-full flex-col gap-6">
    <header class="flex flex-col gap-3 border-b pb-6">
      <div class="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-700 dark:text-cyan-300">
        <CalendarCog class="h-5 w-5" />
        Holiday administration
      </div>
      <div class="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div class="flex flex-col gap-2">
          <h1 class="text-3xl font-semibold tracking-tight">
            Customer holiday schedules
          </h1>
          <p class="max-w-3xl text-sm leading-6 text-muted-foreground">
            Review and manually edit the holiday responses customers have submitted.
          </p>
        </div>
        <form class="flex w-full gap-2 lg:max-w-md" @submit.prevent="searchEntries">
          <Input v-model="store.search" aria-label="Search customer holiday schedules" placeholder="Search company, account, or holiday" />
          <Button type="submit" variant="secondary" :loading="store.isLoading">
            <Search class="h-4 w-4" />
            Search
          </Button>
        </form>
      </div>
    </header>

    <Alert v-if="store.error" variant="destructive">
      <CircleAlert class="h-4 w-4" />
      <AlertTitle>Schedules unavailable</AlertTitle>
      <AlertDescription>{{ store.error }}</AlertDescription>
    </Alert>

    <div class="flex items-center justify-between gap-4 text-sm text-muted-foreground">
      <span>{{ resultSummary }}</span>
      <Button v-if="store.search" type="button" variant="ghost" size="sm" @click="clearSearch">
        Clear search
      </Button>
    </div>

    <div v-if="store.isLoading && store.items.length === 0" class="flex min-h-52 items-center justify-center gap-3 rounded-xl border bg-card text-sm text-muted-foreground">
      <LoaderCircle class="h-5 w-5 animate-spin" />
      Loading customer holiday schedules…
    </div>

    <HolidaySchedule v-else-if="store.items.length > 0" :holidays="store.items" :saving-ids="store.savingIds" row-id-key="customer_holiday_id" show-company allow-locked-editing @edit="editHoliday" />

    <div v-else-if="!store.error" class="rounded-xl border border-dashed bg-card px-6 py-14 text-center">
      <CalendarX class="mx-auto h-9 w-9 text-muted-foreground" />
      <div class="flex flex-col gap-1">
        <h2 class="font-medium">
          No customer holiday schedules found
        </h2>
        <p class="text-sm text-muted-foreground">
          {{ store.search ? 'Try a different search.' : 'Customers have not submitted any holiday schedules yet.' }}
        </p>
      </div>
    </div>

    <nav v-if="store.pagination.lastPage > 1" class="flex items-center justify-center gap-3" aria-label="Customer holiday schedule pages">
      <Button type="button" variant="outline" :disabled="store.pagination.currentPage === 1 || store.isLoading" @click="loadPage(store.pagination.currentPage - 1)">
        <ChevronLeft class="h-4 w-4" />
        Previous
      </Button>
      <span class="text-sm text-muted-foreground">Page {{ store.pagination.currentPage }} of {{ store.pagination.lastPage }}</span>
      <Button type="button" variant="outline" :disabled="store.pagination.currentPage === store.pagination.lastPage || store.isLoading" @click="loadPage(store.pagination.currentPage + 1)">
        Next
        <ChevronRight class="h-4 w-4" />
      </Button>
    </nav>

    <Dialog :open="isEditDialogOpen" @update:open="handleEditDialogOpen">
      <DialogContent class="max-h-[90vh] max-w-xl overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Edit holiday schedule</DialogTitle>
          <DialogDescription>Update the customer's submitted holiday schedule.</DialogDescription>
        </DialogHeader>
        <HolidayScheduleCard v-if="selectedHoliday" :key="selectedHoliday.customer_holiday_id" :holiday="selectedHoliday" :saving="store.savingIds.includes(selectedHoliday.customer_holiday_id)" :record-id="selectedHoliday.customer_holiday_id" :context-label="companyLabel(selectedHoliday)" allow-locked-editing @save="saveHoliday" />
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { toast } from 'vue-sonner'
import { CalendarCog, CalendarX, ChevronLeft, ChevronRight, CircleAlert, LoaderCircle, Search } from 'lucide-vue-next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import HolidayScheduleCard from '../components/HolidayScheduleCard.vue'
import HolidaySchedule from '../components/HolidaySchedule.vue'
import { useHolidaysPublicAdminStore } from '../stores/holidaysPublicAdminStore.js'

const store = useHolidaysPublicAdminStore()
const resultSummary = computed(() => `${store.pagination.total} saved ${store.pagination.total === 1 ? 'schedule' : 'schedules'}`)
const selectedHoliday = ref(null)
const isEditDialogOpen = ref(false)

const companyLabel = holiday => {
  const accountNumber = holiday.company.startel_account_number

  return accountNumber ? `${holiday.company.name} · ${accountNumber}` : holiday.company.name
}

const loadPage = async page => {
  try {
    await store.fetchEntries(page)
  } catch {
  }
}

const searchEntries = () => loadPage(1)

const clearSearch = () => {
  store.search = ''
  loadPage(1)
}

const saveHoliday = async ({ recordId, schedule }) => {
  try {
    await store.saveHoliday(recordId, schedule)
    toast.success('Customer holiday schedule updated.')
    isEditDialogOpen.value = false
    selectedHoliday.value = null
  } catch {
    toast.error(store.error)
  }
}

const editHoliday = holiday => {
  selectedHoliday.value = holiday
  isEditDialogOpen.value = true
}

const handleEditDialogOpen = open => {
  isEditDialogOpen.value = open

  if (!open) {
    selectedHoliday.value = null
  }
}

onMounted(() => loadPage(1))
</script>
